import { EmotionEntry, storageService } from './storageService';

export interface EmotionalAlert {
  id: string;
  childId: string;
  childName: string;
  type: 'sadness_pattern' | 'anxiety_pattern' | 'anger_pattern' | 'low_activity' | 'abuse_or_violence' | 'self_harm' | 'medium_risk' | 'high_risk' | 'emotional_concern' | string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  date: Date;
  acknowledged: boolean;
}

class NotificationService {
  private readonly ALERTS_KEY = 'calmi_alerts';

  // Analizar emociones de un menor y detectar patrones preocupantes
  analyzeEmotions = (childId: string, childName: string, emotions: EmotionEntry[]): EmotionalAlert[] => {
    const alerts: EmotionalAlert[] = [];
    
    if (emotions.length < 3) return alerts; // Necesitamos al menos 3 registros

    // Detectar patrón de tristeza (3 o más emociones tristes en los últimos 7 días)
    const sadEmotions = emotions.filter(e => 
      ['sad', 'tired'].indexOf(e.emotion) !== -1
    );
    
    if (sadEmotions.length >= 3) {
      alerts.push({
        id: Date.now().toString(),
        childId,
        childName,
        type: 'sadness_pattern',
        severity: sadEmotions.length >= 5 ? 'high' : 'medium',
        message: `${childName} ha reportado sentirse triste o cansado ${sadEmotions.length} veces recientemente. Considera hablar con él/ella.`,
        date: new Date(),
        acknowledged: false
      });
    }

    // Detectar patrón de ansiedad (3 o más emociones ansiosas en los últimos 7 días)
    const anxiousEmotions = emotions.filter(e => e.emotion === 'anxious');
    
    if (anxiousEmotions.length >= 3) {
      alerts.push({
        id: (Date.now() + 1).toString(),
        childId,
        childName,
        type: 'anxiety_pattern',
        severity: anxiousEmotions.length >= 5 ? 'high' : 'medium',
        message: `${childName} ha reportado sentirse ansioso ${anxiousEmotions.length} veces recientemente. Podría necesitar apoyo extra.`,
        date: new Date(),
        acknowledged: false
      });
    }

    // Detectar patrón de enojo (3 o más emociones enojadas en los últimos 7 días)
    const angryEmotions = emotions.filter(e => e.emotion === 'angry');
    
    if (angryEmotions.length >= 3) {
      alerts.push({
        id: (Date.now() + 2).toString(),
        childId,
        childName,
        type: 'anger_pattern',
        severity: angryEmotions.length >= 5 ? 'high' : 'medium',
        message: `${childName} ha reportado sentirse enojado ${angryEmotions.length} veces recientemente. Intenta entender qué le molesta.`,
        date: new Date(),
        acknowledged: false
      });
    }

    // Detectar baja actividad (menos de 2 registros en los últimos 7 días)
    if (emotions.length < 2) {
      alerts.push({
        id: (Date.now() + 3).toString(),
        childId,
        childName,
        type: 'low_activity',
        severity: 'low',
        message: `${childName} ha registrado pocas emociones recientemente. Anímalo/a a usar la app.`,
        date: new Date(),
        acknowledged: false
      });
    }

    return alerts;
  };

  // Guardar alerta en localStorage
  saveAlert = (alert: EmotionalAlert): void => {
    const alerts = this.getAlerts();
    alerts.push(alert);
    localStorage.setItem(this.ALERTS_KEY, JSON.stringify(alerts));
  };

  // Obtener todas las alertas
  getAlerts = (): EmotionalAlert[] => {
    const data = localStorage.getItem(this.ALERTS_KEY);
    if (!data) return [];
    
    return JSON.parse(data).map((alert: any) => ({
      ...alert,
      date: new Date(alert.date)
    }));
  };

  // Obtener alertas no reconocidas
  getUnacknowledgedAlerts = (): EmotionalAlert[] => {
    return this.getAlerts().filter(alert => !alert.acknowledged);
  };

  // Obtener alertas de un menor específico
  getChildAlerts = (childId: string): EmotionalAlert[] => {
    return this.getAlerts().filter(alert => alert.childId === childId);
  };

  // Marcar alerta como reconocida
  acknowledgeAlert = (alertId: string): void => {
    const alerts = this.getAlerts();
    const index = alerts.findIndex(alert => alert.id === alertId);
    
    if (index !== -1) {
      alerts[index].acknowledged = true;
      localStorage.setItem(this.ALERTS_KEY, JSON.stringify(alerts));
    }
  };

  // Marcar todas las alertas de un menor como reconocidas
  acknowledgeChildAlerts = (childId: string): void => {
    const alerts = this.getAlerts();
    alerts.forEach(alert => {
      if (alert.childId === childId) {
        alert.acknowledged = true;
      }
    });
    localStorage.setItem(this.ALERTS_KEY, JSON.stringify(alerts));
  };

  // Eliminar alertas antiguas (más de 30 días)
  cleanOldAlerts = (): void => {
    const alerts = this.getAlerts();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentAlerts = alerts.filter(alert => alert.date >= thirtyDaysAgo);
    localStorage.setItem(this.ALERTS_KEY, JSON.stringify(recentAlerts));
  };

  // Generar mensaje de notificación basado en el tipo
  getAlertMessage = (alert: EmotionalAlert): string => {
    const messages: Record<string, string> = {
      sadness_pattern: '😢 Patrón de tristeza detectado',
      anxiety_pattern: '😰 Patrón de ansiedad detectado',
      anger_pattern: '😠 Patrón de enojo detectado',
      low_activity: '📉 Baja actividad en la app',
      abuse_or_violence: '⚠️ Posible abuso o violencia detectada',
      self_harm: '🔴 Riesgo de daño personal detectado',
      high_risk: '🔴 Situación de alto riesgo detectada',
      medium_risk: '🟡 Situación de riesgo medio detectada',
      emotional_concern: '🟡 Preocupación emocional detectada'
    };
    
    return messages[alert.type] || '📢 Alerta emocional';
  };

  // Obtener severidad como emoji
  getSeverityEmoji = (severity: string): string => {
    const emojis = {
      low: '🟢',
      medium: '🟡',
      high: '🔴'
    };
    
    return emojis[severity as keyof typeof emojis] || '⚪';
  };

  // Solicitar permiso para notificaciones del navegador
  requestNotificationPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) return false;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  };

  // Mostrar notificación del navegador al tutor
  showBrowserNotification = (title: string, body: string): void => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if (localStorage.getItem('calmi_profile_mode') !== 'tutor') return;
    
    try {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'calmi-alert'
      });
    } catch (error) {
      console.log('[NOTIFICATION] Error mostrando notificación:', error);
    }
  };

  // Generar alerta y notificar al tutor
  generateAlert = async (childName: string, emotion: string): Promise<void> => {
    const emotions = storageService.getEmotions();
    const existingAlerts = this.getUnacknowledgedAlerts();
    
    // Alertas específicas por emoción
    let alert: EmotionalAlert | null = null;
    
    if (['sad', 'tired'].indexOf(emotion) !== -1) {
      const sadEmotions = emotions.filter(e => ['sad', 'tired'].indexOf(e.emotion) !== -1);
      if (sadEmotions.length >= 3) {
        alert = {
          id: Date.now().toString(),
          childId: 'child',
          childName,
          type: 'sadness_pattern',
          severity: sadEmotions.length >= 5 ? 'high' : 'medium',
          message: `${childName} ha reportado sentirse triste o cansado ${sadEmotions.length} veces recientemente. Considera hablar con él/ella.`,
          date: new Date(),
          acknowledged: false
        };
      }
    }
    
    if (emotion === 'angry') {
      const angryEmotions = emotions.filter(e => e.emotion === 'angry');
      if (angryEmotions.length >= 3) {
        alert = {
          id: (Date.now() + 1).toString(),
          childId: 'child',
          childName,
          type: 'anger_pattern',
          severity: angryEmotions.length >= 5 ? 'high' : 'medium',
          message: `${childName} ha reportado sentirse enojado ${angryEmotions.length} veces recientemente. Intenta entender qué le molesta.`,
          date: new Date(),
          acknowledged: false
        };
      }
    }
    
    if (alert) {
      // Evitar duplicados del mismo tipo en el mismo día
      const today = new Date().toDateString();
      const alreadyExists = existingAlerts.some(a => 
        a.type === alert!.type && new Date(a.date).toDateString() === today
      );
      
      if (!alreadyExists) {
        this.saveAlert(alert);
        await this.requestNotificationPermission();
        this.showBrowserNotification(
          '🔔 Calmi - Alerta del tutor',
          alert.message
        );
      }
    }
  };

  // Crear alerta manualmente desde el chat o el diario
  createAlert = (alert: Omit<EmotionalAlert, 'id' | 'acknowledged'>): void => {
    const existingAlerts = this.getUnacknowledgedAlerts();
    const today = new Date().toDateString();
    
    // Evitar duplicados del mismo tipo el mismo día
    const alreadyExists = existingAlerts.some(a =>
      a.childId === alert.childId && a.type === alert.type && new Date(a.date).toDateString() === today
    );
    
    if (!alreadyExists) {
      const newAlert: EmotionalAlert = {
        ...alert,
        id: Date.now().toString(),
        acknowledged: false
      };
      this.saveAlert(newAlert);
      this.showBrowserNotification(
        '🔔 Calmi - Alerta del tutor',
        alert.message
      );
    }
  };

  // Generar alerta basada en la emoción más común del menor
  generateEmotionAlert = async (childId: string, childName: string): Promise<void> => {
    const emotions = storageService.getEmotions();
    const stats = storageService.getEmotionStats(7);
    
    let mostCommonEmotion = '';
    let maxCount = 0;
    const emotionKeys = Object.keys(stats);
    for (const emotion of emotionKeys) {
      if (stats[emotion] > maxCount) {
        maxCount = stats[emotion];
        mostCommonEmotion = emotion;
      }
    }
    
    if (maxCount < 2) return;
    
    let type: string = 'emotional_concern';
    let severity: 'low' | 'medium' | 'high' = 'low';
    let message = '';
    
    switch (mostCommonEmotion) {
      case 'sad':
      case 'tired':
        type = 'sadness_pattern';
        severity = maxCount >= 4 ? 'high' : 'medium';
        message = `${childName} ha registrado ${maxCount} veces tristeza o cansancio esta semana. Es importante que un adulto se acerque a hablar con él/ella.`;
        break;
      case 'anxious':
      case 'scared':
        type = 'anxiety_pattern';
        severity = maxCount >= 4 ? 'high' : 'medium';
        message = `${childName} ha registrado ${maxCount} veces ansiedad o miedo esta semana. Considera apoyarle con calma.`;
        break;
      case 'angry':
        type = 'anger_pattern';
        severity = maxCount >= 4 ? 'high' : 'medium';
        message = `${childName} ha registrado ${maxCount} veces enojo esta semana. Trata de entender qué le molesta.`;
        break;
      case 'happy':
        return;
      default:
        type = 'emotional_concern';
        severity = 'low';
        message = `${childName} ha registrado la emoción ${mostCommonEmotion} ${maxCount} veces esta semana.`;
    }
    
    this.createAlert({
      childId,
      childName,
      type,
      severity,
      message,
      date: new Date()
    });
  };
}

export const notificationService = new NotificationService();
